import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import UserModel from '@geartrack/shared-lib/dist/User'
import { doc, onSnapshot } from 'firebase/firestore'
import { onAuthStateChanged, User } from 'firebase/auth'
import { authInstance, firestoreInstance } from '../libraries/firebase'
import { UserDocument } from '@geartrack/shared-lib/dist/firebase'
import CustomSpinner from './CustomSpinner'

interface IAuthContext {
  authUser?: User // set if signed in
  user?: UserModel // set if signed in
}

const AuthContext = createContext<IAuthContext>({})

const AuthProvider = props => {
  const [authUser, setAuthUser] = useState<User>(undefined)
  const [user, setUser] = useState<UserModel>(null)

  // firebase user object listener
  useEffect(() => {
    return onAuthStateChanged(authInstance, user => {
      if (user) {
        // User is signed in
        setAuthUser(user)
      } else {
        // User is signed out
        setAuthUser(null)
      }
    })
  }, [])

  // load the user document from firestore (subscribed)
  useEffect(() => {
    if (!authUser?.uid) return

    return onSnapshot(doc(firestoreInstance, 'users', authUser.uid), doc => {
      if (doc.exists()) {
        setUser(UserModel.fromDoc({ id: doc.id, ...doc.data() } as UserDocument))
      }
    })
  }, [authUser?.uid])

  // handle logout
  useEffect(() => {
    if (!authUser && user) {
      setUser(null)
    }
  }, [authUser, user])

  const contextValues: IAuthContext = useMemo(
    () => ({
      user,
      authUser,
    }),
    [user, authUser]
  )

  const isLoading = Boolean(authUser === undefined || (authUser && !user))
  console.log('Rendering auth provider', authUser, user, isLoading)

  return (
    <AuthContext.Provider value={contextValues}>
      {isLoading && <CustomSpinner />}
      {!isLoading && props.children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext<IAuthContext>(AuthContext)

export default AuthProvider
